<template>
    <confirmation-dialog
        :show="show && !loading"
        title=""
        :show-icon="false"
        :proceed-button-text="nextButtonText"
        @proceed="gotoNextSection()"
        :proceed-disabled="form.busy || disableNextButton"
        :show-cancel-button="(!form.busy || creating) && !disableCloseButton"
        :show-proceed-button="!disableCloseButton"
        :cancel-button-text="previousButtonText"
        proceed-button-colour="indigo-600"
        proceed-button-hover-colour="indigo-800"
        @cancelled="gotoPreviousSection()">
        <!-- Show other modal pieces based on current step used -->
        <div slot="title" class="flex flex-col space-y-4 mb-4">
            <div class="flex justify-between items-center">
                <div class="flex items-center space-x-4">
                    <icon-briefcase class="h-10 w-10 text-white bg-indigo-600 p-1 rounded"></icon-briefcase>

                    <div class="text-indigo-600 font-bold text-lg">
                        <span v-if="isCurrentSection(1)">Select Issues</span>
                        <span v-else-if="isCurrentSection(2)">Verify Settings</span>
                        <span v-else-if="isCurrentSection(3)">Select Communication</span>
                        <span v-else-if="isCurrentSection(4)">Update Ticket</span>
                    </div>

                    <portal-target name="titleDropdownPortal" v-show="isCurrentSection(3)"></portal-target>
                </div>

                <button class="text-md text-gray-400 hover:text-indigo-600 transition duration-300 p-1" v-show="!disableCloseButton" @click="gotoPreviousSection(true)"
                        :disabled="form.busy || disableCloseButton">x
                </button>
            </div>

            <simple-steps :total-steps="totalModalSections" :current-step="currentModalSection"></simple-steps>
        </div>

        <task-service-provider-modal-select-issues
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(1)"
            :ticket-id="mutableTicket.id"
            :status-object="statusObject"
            :form="form"
            @toggle-next-button="toggleNextButton"
            slot="body">
        </task-service-provider-modal-select-issues>

        <task-service-provider-modal-select-task-settings
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(2)"
            :form="form"
            slot="body">
        </task-service-provider-modal-select-task-settings>

        <task-service-provider-modal-communication
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(3) "
            :form="form"
            :ticket-id="mutableTicket.id"
            :status-object="statusObject"
            :communication-scenarios="communicationScenarios"
            :current-service-providers="currentServiceProviders"
            @toggle-close-button="toggleCloseButton()"
            slot="body">
        </task-service-provider-modal-communication>

        <task-service-provider-modal-summary
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(4)"
            :form="form"
            :portfolio-id="form.ticket_details.portfolio_id"
            :ticket-phases="ticketPhases"
            slot="body">
        </task-service-provider-modal-summary>
    </confirmation-dialog>
</template>

<script>
import MultiSectionModal from "../../../mixins/multi-section-modal";

export default {
    name: "TaskServiceProviderModalBase",

    mixins: [MultiSectionModal],

    props: {
        ticket: {
            required: true,
            type: Object,
        },
        show: {
            required: true,
            type: Boolean,
        },
        statusObject: {
            required: false,
            type: Object,
            default: null,
            description: "Contains Service Provider and Status - Used when Quote Approved/Rejected button is used to pre-populate modal"
        },
        currentServiceProviders: {
            required: false,
            type: Array,
            default: null,
            description: "List of current service providers linked to the ticket"
        },
    },

    data () {
        return {
            mutableTicket: this.ticket,
            loading: true,
            creating: false,
            form: new AppForm({
                ticket_details: {},
                task_settings: {},
                todo: {},
                service_providers: [],
                selected_issues: [],
                errors: {},
            }),
            ticketPhases: [],
            defaultReminderDays: {},
            communicationScenarios: [],
            disableCloseButton: false,
        };
    },

    created () {
        this.finalButtonText = "Task & Send";
        this.totalModalSections = 4;

        this.refreshModal();

        let self = this;
        Bus.$on("final-click", function () {
            self.taskServiceProvider();
        });
    },

    watch: {
        statusObject (val) {
            val && val.serviceProvider ? this.form.service_providers = [val.serviceProvider] : this.form.service_providers.splice(0);

            if (val && val.status === "quote rejected") {
                this.currentModalSection = 3;
                this.form.task_settings.show_landlord_details = false;
                this.form.task_settings.show_tenant_details = false;
                this.form.task_settings.requires_quality_assurance_sign_off = false;
                this.form.task_settings.generate_pdf = false;
                this.form.task_settings.include_photos = false;
                this.form.task_settings.include_signatures = false;
                this.form.task_settings.instruction_message = "";
            }

            if (!this.loading && !this.loadingCommScenarios) {
                this.getCommunicationScenarios()
                .then(() => this.loading = false);
            }
        },

        creating (newVal) {
            this.finalButtonText = newVal ? "Working..." : "Task & Send";
            this.setNextButtonText();
        },

        "ticket.next_todo": function () {
            this.refreshModal();
        },

        "form.todo.reminder_date": function (newDate) {
            if (!newDate) {
                this.form.todo.users.splice(0);
            } else if (this.form.todo.users && this.form.todo.users.length === 0) {
                this.form.todo.users.push({id: this.state.user.id, name: this.state.user.name});
            }
        },

        "form.ticket_details.priority": function (priority) {
            this.setTodoReminderDateBasedOnPriority(priority);
        },
    },

    methods: {
        refreshModal () {
            this.setupForm()
            .then(() => this.getCommunicationScenarios()
            .then(() => this.loading = false
            ));
        },

        setupForm () {
            return new Promise((resolve, reject) => {
                this.loading = true;
                this.currentModalSection = 1;

                // Triggers v-if, re-rendering out custom savedReplyNotificationInput
                this.$set(this.form, "ticket_notification_settings", {reanimate: true});

                axios.get("/settings/tickets/show")
                .then((response) => {
                    this.ticketPhases = response.data.phases.map(function (phase) {
                        return {id: phase.id, phase: phase.phase};
                    });
                    this.defaultReminderDays = {
                        low: response.data.settings.reminder_in_days_low,
                        medium: response.data.settings.reminder_in_days_medium,
                        high: response.data.settings.reminder_in_days_high
                    };

                    let todoUsers = [];
                    let reminderDate = this.setTodoReminderDateBasedOnPriority(this.ticket.priority, true);
                    let todoId = null;
                    if (this.mutableTicket.next_todo) {
                        todoId = this.mutableTicket.next_todo.id;
                        todoUsers = this.mutableTicket.next_todo.users && this.mutableTicket.next_todo.users.length > 0 ?
                            this.mutableTicket.next_todo.users.map(function (user) {
                                return {id: user.id, name: user.name};
                            }) : null;
                        if (this.mutableTicket.next_todo.latest_reminder) {
                            reminderDate = this.mutableTicket.next_todo.latest_reminder.reminder_date;
                        }
                    }

                    this.form = new AppForm({
                        ticket_details: {
                            phase_id: this.mutableTicket.phase_id,
                            portfolio_id: this.selectedAsset ? this.selectedAsset.portfolio_id : null,
                        },

                        task_settings: {
                            show_landlord_details: response.data.settings.task_card_include_landlord_details,
                            show_tenant_details: response.data.settings.task_card_include_tenant_details,
                            requires_quality_assurance_sign_off: response.data.settings.task_card_requires_quality_assurance_sign_off,
                            generate_pdf: response.data.settings.task_card_generate_pdf,
                            include_photos: response.data.settings.task_card_include_issue_photos,
                            include_signatures: response.data.settings.task_card_include_signature_lines,
                            instruction_message: "",
                        },

                        todo: {
                            id: todoId,
                            reminder_date: reminderDate,
                            users: todoUsers,
                        },

                        ticket_notification_settings: {
                            tenant_email_communication: null,
                            tenant_sms_communication: null,
                            landlord_email_communication: null,
                            landlord_sms_communication: null,
                        },
                        service_providers: [],
                        selected_issues: [],
                    });
                    resolve();
                })
                .catch((error) => {
                        toastr.error(error.message);
                        reject();
                    }
                );
            });
        },

        getCommunicationScenarios () {
            return new Promise((resolve, reject) => {
                this.loading = true;
                this.loadingCommScenarios = true;

                let scenario = ["requesting quote", "tasking service provider", "approving quote", "rejecting quote"];

                // when explicitly approving: remove rejecting --- when explicitly rejecting: remove approving
                if (this.statusObject) {
                    switch (this.statusObject.status) {
                        case ("quote approved"):
                            let rejectIndex = scenario.indexOf("rejecting quote");
                            if (rejectIndex !== -1) {
                                scenario.splice(rejectIndex, 1);
                            }
                            break;
                        case ("quote rejected"):
                            let approveIndex = scenario.indexOf("approving quote");
                            if (approveIndex !== -1) {
                                scenario.splice(approveIndex, 1);
                            }
                            break;
                    }
                } else {
                    let rejectIndex = scenario.indexOf("rejecting quote");
                    if (rejectIndex !== -1) {
                        scenario.splice(rejectIndex, 1);
                    }
                    let approveIndex = scenario.indexOf("approving quote");
                    if (approveIndex !== -1) {
                        scenario.splice(approveIndex, 1);
                    }

                    let phaseIndex = this.ticketPhases.findIndex(p => p.phase === "Awaiting Quote");
                    if (phaseIndex !== -1) {
                        this.form.ticket_details.phase_id = this.ticketPhases[phaseIndex].id;
                    }
                }

                axios.get("/communication-scenarios/search", {
                    params: {
                        scenario: scenario
                    }
                })
                .then(response => {
                    this.communicationScenarios = response.data;
                    this.loadingCommScenarios = false;
                    resolve();
                })
                .catch(error => {
                        this.loadingCommScenarios = false;
                        toastr.error(error.message);
                        reject();
                    }
                );
            });
        },

        taskServiceProvider () {
            this.creating = true;

            if (this.form.service_providers && this.form.service_providers.length > 0) {
                this.form.service_providers.forEach(sp => {
                    if (sp.communication_channels) {
                        sp.communication_channels.forEach(channel => {
                            if (channel.saved_reply) {
                                if (channel.saved_reply.body) {
                                    channel.saved_reply.custom_body = channel.saved_reply.body;
                                }
                                if (channel.saved_reply.id) {
                                    channel.saved_reply.saved_reply_id = channel.saved_reply.id;
                                }
                            }
                        });
                    }
                });
            }

            App.post(`/tickets/${this.mutableTicket.id}/service-providers/taskServiceProviders`, this.form)
            .then((response) => {
                let emitUpdate = false;
                if (this.form.ticket_details.phase_id !== this.mutableTicket.phase_id) {
                    this.$set(this.mutableTicket, "phase_id", this.form.ticket_details.phase_id);
                    emitUpdate = true;
                }

                if (this.mutableTicket.next_todo && this.mutableTicket.next_todo.latest_reminder) {
                    // NextTodo was either created, updated, or archive, reflect this on the frontend
                    if (this.form.todo.reminder_date) {
                        this.$set(this.mutableTicket.next_todo.latest_reminder, "reminder_date", this.$dayjs(this.form.todo.reminder_date).format("YYYY-MM-DD"));
                        this.mutableTicket.next_todo.users.splice(0);
                    } else {
                        this.$set(this.mutableTicket, "next_todo", null);
                    }
                    this.form.todo.users.forEach(user => {
                        this.mutableTicket.next_todo.users.push(user);
                    });
                    emitUpdate = true;
                } else if (this.form.todo.reminder_date && !this.mutableTicket.next_todo) {
                    this.mutableTicket.next_todo = response.nextTodo;
                    emitUpdate = true;
                }

                if (emitUpdate) {
                    Bus.$emit("ticket-updated", this.mutableTicket);
                }

                if (this.form.task_settings.generate_pdf) {
                    Bus.$emit("task-card-added");
                }
                this.$emit("service-provider-tasked", response.assignedServiceProvider);
                this.$emit("close-modal");
                this.creating = false;
                this.refreshModal();
                toastr.success(response.message);

                this.$nextTick(function () {
                    // update ticket main details form
                    Bus.$emit("service-provider-tasked");
                });
            })
            .catch(errors => {
                this.creating = false;
                toastr.error(errors.message);
                this.stepToError(errors);
            });
        },

        toggleNextButton (disable) {
            this.disableNextButton = disable;
        },

        toggleCloseButton () {
            this.disableCloseButton = !this.disableCloseButton;
        },

        setTodoReminderDateBasedOnPriority (priority, shouldReturn = false) {
            let interval = 0;
            switch (priority) {
                case ("Low"):
                    interval = this.defaultReminderDays.low;
                    break;
                case ("Medium"):
                    interval = this.defaultReminderDays.medium;
                    break;
                case ("High"):
                    interval = this.defaultReminderDays.high;
                    break;
            }
            let date = this.$dayjs().add(interval, "days").format("YYYY-MM-DD");
            if (shouldReturn) {
                return date;
            } else {
                this.$set(this.form.todo, "reminder_date", date);
            }
        },

        stepToError (errors) {
            let errorKey = Object.keys(errors.errors).toString();
            switch (true) {
                case errorKey.startsWith("selected_issues"):
                    this.goToSpecificSection(1);
                    break;
                case errorKey.startsWith("task_settings"):
                    this.goToSpecificSection(2);
                    break;
                case errorKey.startsWith("service_providers"):
                    this.goToSpecificSection(3);
                    break;
                case errorKey.startsWith("ticket_notification_settings"):
                    this.goToSpecificSection(3);
                    break;
                case errorKey.startsWith("ticket_details"):
                    this.goToSpecificSection(4);
                    break;
            }
        },
    },
};
</script>

<style scoped>

</style>
